import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { Link, useNavigate } from "react-router-dom";
import { MdEmail } from "react-icons/md";
import { RiLockPasswordLine } from "react-icons/ri";
import { appPrimaryColor } from "../utils/constants";
import { toast } from "react-toastify";
import { getFarmerDetails } from "../api";
import InnerLoader from "../components/microComponents/InnerLoader";
import { getUser } from "../utils";

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (user?.farmer) {
      navigate("/farmer-dashboard");
      return;
    } else if (user?.org) {
      navigate("/");
      return;
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoader(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email?.trim(),
        formData.password
      );

      if (userCredential.user) {
        const response = await getFarmerDetails(userCredential.user?.accessToken);
        if (response) {
          // if ( response?.farmer?.role !== 'super_admin')

          const userData = { ...userCredential.user, ...response?.farmer, category: response?.category, ethnicity: response?.ethnicity };
          localStorage.setItem('user', JSON.stringify(userData));

          if (response?.farmer?.role === 'farmer') {
            navigate("/farmer-dashboard", { replace: true });
          } else if (response?.farmer?.role === 'admin' || response?.farmer?.role === 'super_admin') {
            // navigate("/", { replace: true });
            navigate(0)
          } else {
            toast.error("Invalid user type");
          }
        } else {
          toast.error("Failed to get user details.");
        }
      }
    } catch (error) {
      console.error("Error signing in:", error);
      toast.error(
        "Failed to sign in. Please check your credentials and try again."
      );
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Background Image */}
      <div className="hidden md:flex md:w-1/2 bg-darkGreen items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h1 className="text-4xl font-bold mb-6">Welcome Back!</h1>
          <p className="text-lg opacity-90">
            Sign in to access your account and manage your agricultural data.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-2xl shadow-lg">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Sign In</h2>
            <p className="mt-2 text-sm text-gray-600">
              Login to access your account
              {/* Don't have an account?{" "}
              <Link to="/signup" className="text-darkGreen hover:underline font-medium">
                Sign up
              </Link> */}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdEmail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                                             focus:ring-2 focus:ring-darkGreen focus:border-transparent"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <RiLockPasswordLine className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-10 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                                             focus:ring-2 focus:ring-darkGreen focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <span className="text-gray-500 text-sm">
                    {showPassword ? 'Hide' : 'Show'}
                  </span>
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loader}
              className={`w-full flex justify-center items-center gap-2 py-3 px-4 rounded-lg text-white font-medium transition-all
              ${loader ? 'bg-gray-400 cursor-not-allowed' : `${appPrimaryColor} hover:opacity-90`}`}
            >
              {loader ? (
                <>
                  <InnerLoader
                    divSize={{ width: "20px", height: "20px" }}
                    loaderSize={{ width: "20px", height: "20px" }}
                  />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-darkGreen hover:underline">
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;